<template>
  <div
    class="flex flex-wrap lg:flex-no-wrap relative p-4 lg:px-6 bg-grey-lighter rounded-sm"
  >
    <!-- SALETAG -->

    <SaleTag
      :sale="product.is_sale"
      :bestseller="product.is_bestseller"
      :news="news"
    />

    <!-- PRODUCT CAROUSEL -->
    <template v-for="variant in product.variants">
      <ProductCarousel
        v-if="selectedVariant && selectedVariant.id === variant.id"
        :key="variant.id"
        class="h-132 w-full lg:w-half mr-0 lg:mr-3"
        :images="variant.images"
      />
    </template>

    <!-- INFORMATION PART -->

    <div class="w-full lg:w-half ml-0 lg:ml-3">
      <!-- FIRST BLOCK - NAME AND PRICE -->

      <div class="horizon-line mb-3 pb-3">
        <nuxt-link
          class="inline-block mb-6 py-3 px-5 bg-white hover:bg-grey-light rounded-sm hover:cursor-pointer transition"
          :to="product.brand.url"
        >
          <img class="w-20" :src="product.brand.logo" alt="brand logo" />
        </nuxt-link>
        <h3 class="mb-2 text-2xl hyphens">
          {{ product.title
          }}{{ selectedVariant && ` – ${selectedVariant.title}` }}
        </h3>
        <div
          class="inline-block text-lg font-semibold tracking-wide uppercase text-grey-darker -mb-5"
        >
          {{ price | currency }}
          <span v-if="product.is_pair" class="font-medium normal-case text-base"
            >/ Paar</span
          >
        </div>
        <div v-if="priceOld" class="inline-block mx-2">
          <span
            class="text-xs font-semibold tracking-wide uppercase text-grey line-through"
          >
            {{ priceOld | currency }}
          </span>
        </div>

        <span class="font-semibold text-xs text-green">{{
          product.availability
        }}</span>
      </div>

      <!-- SECOND BLOCK - SPECIFIC PROPRIETIES -->

      <div
        v-if="
          product.variants && product.variants.length > 0 && selectedVariant
        "
        class="horizon-line mb-3"
      >
        <h4 class="mb-2">Varianten</h4>
        <div class="flex flex-wrap -mr-2">
          <Color
            v-for="variant in product.variants"
            :key="variant.id"
            v-model="selectedVariant"
            :color="variant.color"
            :option-value="variant"
            name="variant-selector"
          />
        </div>
      </div>
      <div
        v-if="
          product.versions && product.versions.length > 0 && selectedVersion
        "
        class="horizon-line mb-4 pb-3"
      >
        <h4 class="mb-2">Versionen</h4>
        <Version
          v-for="version in product.versions"
          :key="version.id"
          v-model="selectedVersion"
          :option-value="version"
          name="version-selector"
        >
          {{ version.title }}
        </Version>
      </div>

      <!-- THIRD BLOCK - STOCK AND STATE -->

      <div class="text-grey-darker">
        <div v-if="product.warranty" class="flex items-center mb-2">
          <img
            class="w-4 mr-2"
            src="~assets/images/ill/warranty.svg"
            alt="warranty icon"
          />
          <span class="font-semibold text-xs tracking-wide uppercase">
            {{ product.warranty }}
          </span>
        </div>
        <div v-if="product.product_condition" class="flex items-center mb-2">
          <img
            class="w-4 mr-2"
            src="~assets/images/ill/original.svg"
            alt="original icon"
          />
          <span class="font-semibold text-xs tracking-wide uppercase">
            {{ product.product_condition }}
          </span>
        </div>
      </div>

      <!-- ADD TO CART BUTTON -->

      <div v-if="!isInOutletRegime">
        <ClassicButton
          v-if="price && price > 0"
          class="mt-5 mb-6"
          href="/product"
          @click.native="addToCart"
        >
          In den Warenkorb
        </ClassicButton>

        <div v-else class="mt-2">
          <p>Preis auf Anfrage</p>
        </div>
      </div>
      <div v-else class="mt-6 px-6 py-5 callout flex flex-col">
        <span class="text-xs leading-custom">
          Um dieses hochwertige Produkt zu erleben, laden wir Sie herzlich dazu
          ein in einer Filiale von uns vorbeizukommen. Wir unterstützen Sie
          gerne bei der Individualisierung und dem Kauf des Produkts.
        </span>
        <NuxtLink
          to="/ueber-uns#standorte"
          class="block text-red text-xs mt-4 ml-auto self-end uppercase font-bold"
          >Filiale finden</NuxtLink
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ProductCarousel from '~/components/carousel/ProductCarousel'
import Color from '~/components/checkboxes/Color'
import Version from '~/components/checkboxes/Version'
import ClassicButton from '~/components/ClassicButton'
import SaleTag from '~/components/SaleTag'

export default {
  components: {
    ProductCarousel,
    Color,
    Version,
    ClassicButton,
    SaleTag,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    sale: {
      type: Boolean,
      default: false,
    },
    bestseller: {
      type: Boolean,
      default: false,
    },
    news: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedVariant: this.getDefaultSelectedVariant(),
      selectedVersion: this.getDefaultSelectedVersion(),
    }
  },

  computed: {
    currentImages() {
      if (this.selectedVariant) {
        return this.selectedVariant.images
      }
      return []
    },
    price() {
      if (this.selectedVariant && this.selectedVariant.price > 0) {
        return this.selectedVariant.price
      }
      return this.product.price
    },
    priceOld() {
      let old = this.product.price_old
      if (this.selectedVariant && this.selectedVariant.price_old > 0) {
        old = Math.max(this.selectedVariant.price_old, old)
      }
      if (old && old > 0 && old > this.price) {
        return old
      }
      return null
    },
    skuId() {
      return this.$store.getters['cart/skuByItem']({
        product: this.product,
        variant: this.selectedVariant,
        version: this.selectedVersion,
      })
    },
    isInOutletRegime() {
      return this.product.brand?.slug === 'bang-olufsen'
    },
  },

  jsonld() {
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      productID: this.product.id,
      sku: this.skuId,
      name: this.product.title,
      description: this.product.summary,
      url: `${process.env.NUXT_ENV_APP_URL}${this.$route.fullPath}`,
      image:
        (this.product.meta && this.product.meta.image) || this.product.image,
      brand: this.product.brand && this.product.brand.name,
      offers: [
        {
          '@type': 'Offer',
          price: `${this.price}`,
          priceCurrency: 'CHF',
          itemCondition: 'https://schema.org/NewCondition',
          availability: 'https://schema.org/InStock',
        },
      ],
    }
  },

  mounted() {
    this.$fb.track('ViewContent', {
      content_type: 'product',
      content_ids: [this.product.id],
      content_name: this.product.title,
      value: this.price,
      currency: 'CHF',
      page_title: this.product.title,
    })
  },

  methods: {
    ...mapActions('cart', ['addItem']),

    addToCart() {
      this.addItem({
        product: this.product,
        variant: this.selectedVariant,
        version: this.selectedVersion,
      })

      this.$fb.track('AddToCart', {
        content_type: 'product',
        content_ids: [this.product.id],
        content_name: this.product.title,
        value: this.price,
        currency: 'CHF',
        page_title: this.product.title,
      })
    },

    getDefaultSelectedVariant() {
      if (this.$route.query.c) {
        const variant = this.product.variants.find(
          (variant) => variant.slug === this.$route.query.c
        )

        if (variant) {
          return variant
        }
      }
      return this.product.variants && this.product.variants.length > 0
        ? this.product.variants[0]
        : null
    },

    getDefaultSelectedVersion() {
      if (this.$route.query.v) {
        const version = this.product.versions.find(
          (version) => version.slug === this.$route.query.v
        )

        if (version) {
          return version
        }
      }
      return this.product.versions && this.product.versions.length > 0
        ? this.product.versions[0]
        : null
    },
  },
}
</script>

<style lang="scss" scoped>
h3,
h4 {
  @apply font-semibold tracking-wide uppercase;

  line-height: 26px;
}

h4 {
  @apply text-sm;
}

a {
  &:hover {
    @apply text-black;

    cursor: pointer;

    .arrow {
      @apply border-black;

      transform: translate(5px) rotate(45deg);
    }
  }
}

p {
  @apply mb-6;
}

p,
li {
  @apply text-sm leading-custom;
}

p.intro {
  @apply font-semibold text-base;
}

p:last-of-type {
  margin-bottom: 0;
}

@screen lg {
  .title {
    @apply text-3xl;
  }
  .sub-title {
    @apply text-2xl;
  }
}

.callout {
  background-color: #c8cfdb4e;
}
</style>
